import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { useRouteContext, useRouter } from '@tanstack/react-router';
import { i18n } from '@lingui/core';
import { getRequestContext } from './data';
import { updateRouterLocale } from '@/router';
import { translateSlug } from './slug-rewrites';

/**
 * Build the path for a given locale, translating slugs as needed.
 * Pure function — no side effects.
 */
function buildLanguagePath(
	lang: PortalLocale,
	pathname: string,
	search: string,
	currentLocale: string,
	portalId: string,
	router: { options: { context?: unknown } },
	currentCampaignId?: string,
): string {
	const segments = pathname.split('/').filter(Boolean);
	const isLocaleFirst = (portalLocales as readonly string[]).includes(segments[0] ?? '');
	const restSegments = isLocaleFirst ? segments.slice(1) : segments;

	let newCampaignSlug: string | undefined;
	if (currentCampaignId) {
		const qc = (router.options.context as { queryClient?: { getQueryData(key: unknown[]): unknown } } | undefined)
			?.queryClient;
		if (qc) {
			const cached = qc.getQueryData([portalId, currentLocale, 'campaigns', currentCampaignId]) as
				| { slugsByLocale?: Record<string, string>; id?: string }
				| undefined;
			newCampaignSlug = cached?.slugsByLocale?.[lang] ?? cached?.id ?? currentCampaignId;
		}
	}

	const newLegalSlug = !currentCampaignId && restSegments[0] ? translateSlug(restSegments[0], lang) : undefined;

	const newRestSegments =
		newCampaignSlug !== undefined
			? [newCampaignSlug, ...restSegments.slice(1)]
			: newLegalSlug !== undefined
				? [newLegalSlug, ...restSegments.slice(1)]
				: restSegments;

	return '/' + [lang, ...newRestSegments].join('/') + search;
}

export function useTranslation() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();

	const changeLanguage = (lang: PortalLocale, currentCampaignId?: string) => {
		loadLandingTranslation(lang);
		if (typeof document !== 'undefined') {
			document.cookie = `language=${lang}; path=/; max-age=31536000`;
		}
		updateRouterLocale(lang);
		if (router.options.context) {
			(router.options.context as { locale: PortalLocale }).locale = lang;
		}

		if (typeof window !== 'undefined') {
			const newPath = buildLanguagePath(
				lang,
				window.location.pathname,
				window.location.search,
				locale,
				portalId,
				router,
				currentCampaignId,
			);
			router.history.push(newPath);
		}

		getRequestContext({ data: lang }).then((ctx) => {
			if (router.options.context) {
				Object.assign(router.options.context, ctx);
			}
		});
	};

	return { language: locale, changeLanguage };
}

/**
 * Compute the href for each locale option.
 * Used to render language switcher items as real `<a>` links for preloading and SEO.
 */
export function useLanguageHrefs(locales: readonly PortalLocale[]): Record<string, string> {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();

	// Use router.history.location (available on both server and client) instead of
	// window.location to avoid a server/client branch that causes hydration mismatches.
	const { pathname, search } = router.history.location;

	const hrefs: Record<string, string> = {};
	for (const lang of locales) {
		hrefs[lang] = buildLanguagePath(lang, pathname, search, locale, portalId, router);
	}
	return hrefs;
}

export const landingLocales = ['es', 'en', 'ca', 'it'] as const;
export type LandingLocale = (typeof landingLocales)[number];

export async function loadLandingTranslation(locale: PortalLocale) {
	const { messages } = await import(`../locales/${locale}.po`);
	i18n.loadAndActivate({ locale, messages });
	return i18n;
}
